// The mock draft (MOCK_DRAFT.md, scenario C10, BACKLOG D16).
//
// THREE STEPS ON ONE ROUTE: three questions and Start, beside my kept mocks;
// the room, the same one the live draft uses, with a bot in every other seat;
// and the grades when it ends, with Run another beside them. The kit is read
// once and kept for every mock the visit runs; the room runs here, on a clock
// of its own (`data/mockDraft.ts`), so a mock costs the server nothing per
// pick, and one request to keep it once it ends (§6.1).
//
// SAID BEFORE START: what is kept, for how long, who sees it, and that a mock
// left before its last pick is gone (§3.1). Those are the things a person
// would be surprised by afterward.
//
// AND THE MOCK WITH THE LEAGUE (§6.2) beside the kept ones: the one open to
// join, or a clock and Open, with what opening one says to the league said
// before it is pressed.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LEAGUE_MOCK_CLOCKS,
  formatInZone,
  MOCK_KEPT_DAYS,
  MOCKS_KEPT,
  type DraftKit,
  type SaveMockRequest,
  type SavedMockSummary,
  type SavedMocksView,
} from '@fsp/domain';
import { messageOf } from '../data/api';
import { deviceZone, useLeagueBase, useMockKit, useReaderZone } from '../data/hooks';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useLeagueSource } from '../data/leagueSource';
import { openLeagueMock, useLeagueMock } from '../data/leagueMock';
import { intentId } from '../data/liveDraft';
import { useMockDraft, type MockSetup } from '../data/mockDraft';
import { keepMock, shareMock, useSavedMocks } from '../data/savedMocks';
import { useSession } from '../data/session';
import { EmptyState } from '../kit';
import { Panel } from '../kit/Panel';
import {
  Button,
  Caption,
  Chip,
  Chips,
  Line,
  ListRow,
  OneOf,
  RowName,
  RowSub,
  Stack,
  Text,
  tell,
} from '../kit/ui';
import { useTier } from '../shell/useTier';
import { deviceNow, useTicking } from './DraftRoomScreen';
import { DraftRoom, type RoomActions } from './draft/DraftRoom';
import { MockGrades, gradesOfRecap } from './draft/MockGrades';
import '../styles/screens/predraft.css';
import '../styles/screens/draft-room.css';

export function MockDraftScreen() {
  const source = useLeagueSource();
  if (source !== 'api') {
    return (
      <EmptyState>
        A mock draft runs in your own leagues&rsquo; settings. The practice league has its own draft
        room to try.
      </EmptyState>
    );
  }
  return <RealMock />;
}

/** A league's states past its draft, where no mock is left to run. */
const DRAFTED: readonly string[] = ['in-season', 'playoffs', 'complete', 'archived'];

/** A fresh number for each mock, so no two run alike even from the same seat. */
function nextRun(): number {
  return Math.floor(Math.random() * 1_000_000_000);
}

/** Six months, said as months when it is a whole number of them. */
function keptFor(days: number): string {
  return days % 30 === 0 ? `${String(days / 30)} months` : `${String(days)} days`;
}

function RealMock() {
  // ASKED ONLY WHERE IT CAN BE ANSWERED: a league past its draft is refused
  // the kit every time (`mockKitOf`), and its card already says it has
  // drafted, so the page says so without the read the server would refuse
  // (as a league in setup no longer asks for Home, 2026-10-16).
  const league = useActiveLeague();
  const drafted = league !== null && DRAFTED.includes(league.league.status);
  const { snapshot, reload } = useMockKit(league !== null && !drafted);
  const { session } = useSession();
  const base = useLeagueBase();
  const { leagueId } = useParams();
  const kept = useSavedMocks(leagueId ?? null);
  const tier = useTier();
  // A RUNNING MOCK KEEPS THE KIT IT STARTED WITH. The read can be asked again
  // under it — a pull to refresh asks every read on the page — and a new kit,
  // or a failed read on a train, must not rebuild the room or take it away.
  const [running, setRunning] = useState<{ kit: DraftKit; setup: MockSetup } | null>(null);

  if (running !== null && session !== null) {
    return (
      <MockRoom
        // A NEW RUN IS A NEW ROOM, and whatever was said about keeping the
        // last one goes with it.
        key={running.setup.run}
        kit={running.kit}
        setup={running.setup}
        me={{ userId: session.userId, name: session.displayName }}
        onNew={() => setRunning(null)}
        onRestart={() =>
          setRunning((r) => (r === null ? r : { ...r, setup: { ...r.setup, run: nextRun() } }))
        }
      />
    );
  }

  const list = kept.snapshot.state === 'ready' ? kept.snapshot.value : null;
  const kit = snapshot.state === 'ready' ? snapshot.value : null;
  const columns = tier === 'phone' ? 'fsp-predraft-column' : 'fsp-mock-columns';
  // MOCK WITH THE LEAGUE ONLY BESIDE A KIT: a league whose draft cannot be
  // mocked (an auction, one drafted already) has no room for one either.
  const keptPanel = (
    <>
      {leagueId && snapshot.state === 'ready' ? (
        <TogetherPanel leagueId={leagueId} base={base} />
      ) : null}
      <KeptMocks view={list} failed={kept.snapshot.state === 'failed'} base={base} />
    </>
  );

  if (kit === null || session === null) {
    const failed = !drafted && snapshot.state === 'failed';
    // TRY AGAIN ONLY WHERE AGAIN COULD ANSWER (B-392): a league that has
    // drafted, or a draft no mock can stand in for, is refused the same way
    // every time, and the button was a press that could never do anything.
    const retry =
      failed &&
      ['unreachable', 'timeout', 'rate_limited', 'unavailable'].includes(snapshot.failure.code);
    return (
      <div className="fsp-screen">
        <div className={`fsp-widgets ${columns}`}>
          <div className="fsp-widget-column">
            <Panel title="Mock draft">
              <Stack gap={2}>
                <Text tone="secondary" block>
                  {drafted
                    ? 'This league has drafted, so there is nothing left to mock.'
                    : failed
                      ? snapshot.failure.message
                      : 'Setting up the board…'}
                </Text>
                {drafted || failed ? (
                  <Line>
                    {retry ? <Button onClick={reload}>Try again</Button> : null}
                    <Button to={`${base}/draft`} variant="quiet">
                      Back to the draft
                    </Button>
                  </Line>
                ) : null}
              </Stack>
            </Panel>
            {tier === 'phone' ? (
              <>
                {keptPanel}
                {/* Room under the last row for the floating chat button. */}
                <div className="fsp-list-chip-clearance" />
              </>
            ) : null}
          </div>
          {tier === 'phone' ? null : <div className="fsp-widget-column">{keptPanel}</div>}
        </div>
      </div>
    );
  }
  return (
    <div className="fsp-screen">
      {/* EACH COLUMN ONE GRID ITEM. Panels side by side in `.fsp-widgets`
          get rows sized from the container rather than their content, and a
          phone drew the setup's foot through the kept list (the pre-draft
          page's note says the same). */}
      <div className={`fsp-widgets ${columns}`}>
        <div className="fsp-widget-column">
          <MockSetupPanel
            kit={kit}
            draftTo={`${base}/draft`}
            onStart={(setup) => setRunning({ kit, setup })}
          />
          {tier === 'phone' ? (
            <>
              {keptPanel}
              {/* Room under the last row for the floating chat button. */}
              <div className="fsp-list-chip-clearance" />
            </>
          ) : null}
        </div>
        {tier === 'phone' ? null : <div className="fsp-widget-column">{keptPanel}</div>}
      </div>
    </div>
  );
}

/** The three questions, and Start (MOCK_DRAFT.md §3). */
function MockSetupPanel({
  kit,
  draftTo,
  onStart,
}: {
  kit: DraftKit;
  draftTo: string;
  onStart: (setup: MockSetup) => void;
}) {
  const n = kit.seats.length;
  const [slot, setSlot] = useState(kit.mySlot ?? 1);
  const [bots, setBots] = useState<MockSetup['bots']>('quick');
  const [clock, setClock] = useState<MockSetup['clock']>('none');
  const seats = Array.from({ length: n }, (_, i) => i + 1);
  return (
    <Panel title="Mock draft">
      <Stack gap={4}>
        <Text block>
          Draft against bots in {kit.league.name}&rsquo;s own settings, from any seat, as many times
          as you like.
        </Text>

        <Stack gap={1}>
          <Caption>Your seat</Caption>
          <Chips
            label="Your seat"
            single
            options={seats.map((s) => ({
              value: String(s),
              label: s === kit.mySlot ? `${String(s)} · yours` : String(s),
            }))}
            value={[String(slot)]}
            onChange={(v) => v[0] && setSlot(Number(v[0]))}
          />
          <Text size="xs" tone="secondary" block>
            {kit.mySlot === null
              ? 'Pick any seat to draft from.'
              : kit.orderFixed
                ? `In the real draft you ${kit.auction ? 'nominate' : 'pick'} ${ordinal(kit.mySlot)}.`
                : `Today the order has you ${kit.auction ? 'nominating' : 'picking'} ${ordinal(kit.mySlot)}. It can still change before the draft.`}
          </Text>
        </Stack>

        <Stack gap={1}>
          <OneOf
            label="Bots"
            value={bots}
            onChange={setBots}
            options={[
              { value: 'quick', label: 'Quick' },
              { value: 'instant', label: 'Instant' },
            ]}
          />
          <Text size="xs" tone="secondary" block>
            {kit.auction
              ? bots === 'quick'
                ? 'A bot’s nomination takes about a second. Every player up runs on the bid clock, so you can bid.'
                : 'Bots nominate at once. Every player up runs on the bid clock, so you can bid.'
              : bots === 'quick'
                ? 'Each bot takes about a second, so you can watch the board fill.'
                : 'The bots pick at once, and the mock waits at your turns.'}
          </Text>
        </Stack>

        <Stack gap={1}>
          <OneOf
            label="Your clock"
            value={clock}
            onChange={setClock}
            options={[
              { value: 'none', label: 'No clock' },
              {
                value: 'league',
                // AN AUCTION'S CLOCK HERE IS THE NOMINATION'S: bids always run on the bid clock.
                label: kit.auction
                  ? `${String(kit.auction.nominationSeconds)} sec`
                  : kit.draftMode === 'live'
                    ? `The league’s (${String(kit.pickSeconds)} sec)`
                    : `${String(kit.pickSeconds)} sec`,
              },
            ]}
          />
          <Text size="xs" tone="secondary" block>
            {kit.auction
              ? clock === 'none'
                ? `Your nomination waits for you. Each player up runs on the ${String(kit.auction.bidSeconds)}-second bid clock.`
                : kit.draftMode === 'live' && kit.whenTheClockExpires === 'off'
                  ? `Your nomination on a ${String(kit.auction.nominationSeconds)}-second clock: at zero it waits, as the league’s does. Each player up runs on the ${String(kit.auction.bidSeconds)}-second bid clock.`
                  : `Your nomination on a ${String(kit.auction.nominationSeconds)}-second clock: at zero the room puts up the top of your queue. Each player up runs on the ${String(kit.auction.bidSeconds)}-second bid clock.`
              : clock === 'none'
                ? 'Your turn waits for you.'
                : kit.draftMode !== 'live'
                  ? `Each of your turns on a ${String(kit.pickSeconds)}-second clock, for practice. At zero the room picks for you.`
                  : kit.whenTheClockExpires === 'off'
                    ? 'The real clock on your turns. At zero it waits, as the league’s does.'
                    : 'The real clock on your turns. At zero the room picks for you, as it will on draft day.'}
          </Text>
        </Stack>

        <Text size="sm" tone="secondary" block>
          A mock is just for you. Each one you finish is kept here for {keptFor(MOCK_KEPT_DAYS)},
          your last {MOCKS_KEPT} in this league, and nobody else sees it unless you share it. Leave
          before the last pick and it is gone.
        </Text>
        <Line>
          <Button variant="primary" onClick={() => onStart({ slot, bots, clock, run: nextRun() })}>
            Start the mock
          </Button>
          <Button to={draftTo} variant="quiet">
            Back to the draft
          </Button>
        </Line>
      </Stack>
    </Panel>
  );
}

/** Mock with the league (§6.2): the one open now to join, or a clock and Open. */
function TogetherPanel({ leagueId, base }: { leagueId: string; base: string }) {
  const navigate = useNavigate();
  const zone = useReaderZone() ?? deviceZone();
  const { snapshot } = useLeagueMock(leagueId);
  const [clock, setClock] = useState<string>('60');
  const [busy, setBusy] = useState(false);
  const open = snapshot.state === 'ready' ? (snapshot.value?.open ?? null) : null;
  const go = () => navigate(`${base}/draft/together`);
  const start = async () => {
    setBusy(true);
    const reply = await openLeagueMock(leagueId, Number(clock) as 30 | 60 | 90);
    setBusy(false);
    if (reply.status === 'ok') go();
    else
      tell(
        messageOf(reply, 'The mock did not open.', 'Could not reach the site. Nothing was opened.'),
        'bad',
      );
  };
  return (
    <Panel title="Mock with the league">
      {snapshot.state === 'failed' ? (
        <Text tone="secondary" block>
          {snapshot.failure.message}
        </Text>
      ) : snapshot.state !== 'ready' ? (
        <Text tone="secondary" block>
          Looking for one…
        </Text>
      ) : open !== null ? (
        <Stack gap={2}>
          <Text block>
            {open.mine ? 'You opened' : `${open.openedBy} opened`} a mock for the league
            {open.status === 'live' ? ', and it is under way.' : '.'}
          </Text>
          <Text size="xs" tone="secondary" block>
            {open.pickSeconds} sec a pick
            {open.closesAt !== null
              ? ` · closes at ${formatInZone(open.closesAt, zone, { hour: 'numeric', minute: '2-digit' })} if nobody starts it`
              : ''}
          </Text>
          <Line>
            <Button variant="primary" onClick={go}>
              {open.status === 'live' ? 'Open the room' : 'Join'}
            </Button>
          </Line>
        </Stack>
      ) : (
        <Stack gap={3}>
          <Text block>
            Draft together: anybody in the league takes a seat, and bots take the rest when it
            starts.
          </Text>
          <OneOf
            label="Clock"
            value={clock}
            onChange={setClock}
            options={LEAGUE_MOCK_CLOCKS.map((s) => ({
              value: String(s),
              label: `${String(s)} sec`,
            }))}
          />
          <Text size="xs" tone="secondary" block>
            Opening one posts it in the league&rsquo;s chat for anybody to join, and you start it.
            Each seat is kept in its drafter&rsquo;s mocks.
          </Text>
          <Line>
            <Button variant="secondary" disabled={busy} onClick={() => void start()}>
              Open a league mock
            </Button>
          </Line>
        </Stack>
      )}
    </Panel>
  );
}

/** My kept mocks here, newest first; each opens on its own page (§6.1). */
function KeptMocks({
  view,
  failed,
  base,
}: {
  view: SavedMocksView | null;
  failed: boolean;
  base: string;
}) {
  const navigate = useNavigate();
  const zone = useReaderZone() ?? deviceZone();
  return (
    <Panel title="Your mocks">
      {view === null ? (
        <Text tone="secondary" block>
          {failed ? 'Your mocks could not be read just now.' : 'Reading your mocks…'}
        </Text>
      ) : view.mocks.length === 0 ? (
        <Text tone="secondary" block>
          Mocks you finish are kept here, for you to come back to.
        </Text>
      ) : (
        <Stack gap={2}>
          <div>
            {view.mocks.map((m) => (
              <KeptRow
                key={m.id}
                mock={m}
                zone={zone}
                onOpen={() => navigate(`${base}/draft/mocks/${m.id}`)}
              />
            ))}
          </div>
          <Text size="xs" tone="secondary" block>
            Your last {view.kept} here are kept, each for {keptFor(view.keptDays)}.
          </Text>
        </Stack>
      )}
    </Panel>
  );
}

function KeptRow({
  mock,
  zone,
  onOpen,
}: {
  mock: SavedMockSummary;
  zone: string;
  onOpen: () => void;
}) {
  return (
    <ListRow
      lead={
        <span className="fsp-dr-grade-sm fsp-disp" aria-label={`Grade ${mock.grade}`}>
          {mock.grade}
        </span>
      }
      trail={mock.sharedAt !== null ? <Chip>Shared</Chip> : undefined}
      onClick={onOpen}
      tall
    >
      <RowName>
        Pick {mock.slot} of {mock.teams} ·{' '}
        {new Date(mock.at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: zone,
        })}
      </RowName>
      <RowSub>{mock.firstPicks.map((p) => p.shortName).join(', ')}</RowSub>
    </ListRow>
  );
}

function ordinal(n: number): string {
  const rem = n % 100;
  const suffix =
    rem >= 11 && rem <= 13
      ? 'th'
      : n % 10 === 1
        ? 'st'
        : n % 10 === 2
          ? 'nd'
          : n % 10 === 3
            ? 'rd'
            : 'th';
  return `${String(n)}${suffix}`;
}

/** What became of a finished mock: on its way to being kept, kept, or not kept and why. */
type Keeping =
  | { state: 'saving' }
  | { state: 'kept'; mock: SavedMockSummary }
  | { state: 'failed'; message: string };

function MockRoom({
  kit,
  setup,
  me,
  onNew,
  onRestart,
}: {
  kit: DraftKit;
  setup: MockSetup;
  me: { userId: string; name: string };
  onNew: () => void;
  onRestart: () => void;
}) {
  const base = useLeagueBase();
  const mock = useMockDraft(kit, setup, me, deviceNow, (r) => tell(r.message, 'bad'));
  // A countdown moves only on my turns with a clock; the rest re-renders on the room.
  useTicking(setup.clock === 'league' ? 250 : 1000);
  const { send, record } = mock;
  const leagueId = kit.league.id;

  // KEPT THE MOMENT IT ENDS, once: the record's key is the run's, so a second
  // send — a retry, or a render that runs the effect twice — is the same mock.
  const [keeping, setKeeping] = useState<Keeping | null>(null);
  const sentFor = useRef<string | null>(null);
  const keep = useCallback(
    (what: SaveMockRequest) => {
      setKeeping({ state: 'saving' });
      void keepMock(leagueId, what).then((reply) =>
        setKeeping(
          reply.status === 'ok'
            ? { state: 'kept', mock: reply.data }
            : {
                state: 'failed',
                message: messageOf(
                  reply,
                  'It could not be kept.',
                  'The site could not be reached, so it was not kept.',
                ),
              },
        ),
      );
    },
    [leagueId],
  );
  useEffect(() => {
    if (record === null || sentFor.current === record.key) return;
    sentFor.current = record.key;
    keep(record);
  }, [record, keep]);

  const [sharing, setSharing] = useState(false);
  const share = (id: string) => {
    if (sharing) return;
    setSharing(true);
    void shareMock(leagueId, id).then((reply) => {
      setSharing(false);
      if (reply.status === 'ok') {
        setKeeping({ state: 'kept', mock: reply.data });
        tell('Shared to the league’s chat.', 'good');
      } else tell(messageOf(reply, 'It could not be shared.'), 'bad');
    });
  };

  const actions: RoomActions = useMemo(
    () => ({
      pick: (playerId, forTeam) =>
        send({
          type: 'draft.pick',
          payload: { intentId: intentId(), playerId, ...(forTeam ? { teamId: forTeam } : {}) },
        }),
      setQueue: (playerIds) =>
        send({ type: 'draft.queue.set', payload: { intentId: intentId(), playerIds } }),
      start: () => undefined,
      pause: () => send({ type: 'draft.pause', payload: { intentId: intentId() } }),
      resume: () => send({ type: 'draft.resume', payload: { intentId: intentId() } }),
      undo: () => send({ type: 'draft.undo', payload: { intentId: intentId() } }),
      setAutopilot: (on, teamId) =>
        send({
          type: 'draft.autopilot.set',
          payload: { intentId: intentId(), on, ...(teamId ? { teamId } : {}) },
        }),
      // AN AUCTION'S, practiced in the browser's room (AUCTION.md §6.4).
      auction: {
        nominate: (playerId, bid, forTeam) =>
          send({
            type: 'draft.nominate',
            payload: {
              intentId: intentId(),
              playerId,
              bid,
              ...(forTeam ? { teamId: forTeam } : {}),
            },
          }),
        bid: (lotId, amount) =>
          send({ type: 'draft.bid', payload: { intentId: intentId(), lotId, amount } }),
        autoBid: (lotId, max) =>
          send({ type: 'draft.bid.auto', payload: { intentId: intentId(), lotId, max } }),
      },
    }),
    [send],
  );

  const endNote =
    keeping === null || keeping.state === 'saving'
      ? 'Mock complete. Keeping it with your mocks…'
      : keeping.state === 'kept'
        ? keeping.mock.sharedAt !== null
          ? 'Mock complete, kept with your mocks and shared to the league’s chat.'
          : 'Mock complete, and kept with your mocks. Nobody else sees it unless you share it.'
        : `Mock complete, but not kept. ${keeping.message}`;

  const footer =
    keeping === null || keeping.state === 'saving' ? null : keeping.state === 'kept' ? (
      keeping.mock.sharedAt === null ? (
        <Line>
          <Button size="xs" loading={sharing} onClick={() => share(keeping.mock.id)}>
            Share to league chat
          </Button>
        </Line>
      ) : null
    ) : record !== null ? (
      <Line>
        <Button size="xs" onClick={() => keep(record)}>
          Try keeping it again
        </Button>
      </Line>
    ) : null;

  return (
    <DraftRoom
      view={mock.view}
      now={mock.now}
      connection="practice"
      actions={actions}
      recapTo={`${base}/draft`}
      teamTo={`${base}/draft`}
      mock={{
        untimed: setup.clock === 'none',
        canTakeBack: mock.canTakeBack,
        takeBack: mock.takeBack,
        startOver: onRestart,
        newMock: onNew,
        endNote,
        grades:
          mock.recap === null ? null : (
            <MockGrades
              grades={gradesOfRecap(mock.recap, mock.view, mock.myTeamId)}
              seats={mock.view.seats}
              owner={null}
              footer={footer}
            />
          ),
      }}
    />
  );
}
